import type { ReleaseNote } from "./types";

/**
 * リリースノートの書き方（PRODUCT_DEV-1243 で日暮さんに直されたことから）
 *
 * - 文面案は「全フラグ ON 版」と「全フラグ OFF 版」の2つを、画面と同じ形（タイトル・主な変更・改善・不具合の修正）で見せる。
 *   「OFF なら載せない」の表で済ませない。
 * - タイトルは全テナント共通の1行で `when` が効かない。フラグの組み合わせはテナントごとにばらばらなので、
 *   フラグの裏の機能を入れない。目玉が無ければ「細かな改善と不具合修正」でよい。
 * - 書き言葉にする（ニュースリリース。「〜ので」「〜できます」の話し言葉を避け、「〜し、」「〜できるようにしました」）。
 * - 載せる前に、その版で新しくなったことかをコミットで確かめる（前からある動きを新機能として書かない）。
 * - 利用者が知らないことの多い既存の能力は、今回の変更とあわせて伝えてよい（例: 英語は議事録も商談記録も）。
 */
export const RELEASE_NOTES: ReleaseNote[] = [
  {
    // PRODUCT_DEV-1243: フラグの組み合わせはテナントごとにばらばらなので、タイトルは汎用の言い方にする
    // （記録一覧の名前の変更を目玉に見せるのも中身に合わない。2026-11-02 日暮さん）。
    // 英語の項目は議事録（minutesJaEnMixed）・対話モード（realtimeTranscribeGptTranscribe）・
    // 音声入力など（transcribeGptTranscribe）の3つがそろったテナントにだけ出す。
    // 載せないもの: 集計ボード（ボードを置いた会社だけ）・「顧客マスタの登録内容」を閉じた見た目の変更・
    // API の返し方の変更（画面は変わらない）・管理者や運営だけの変更（パワーユーザー・データ出力・プリセットのヒント）・
    // 記録一覧を開いたときのタブの選び方の直し（前から最後のタブで開いていた）。
    version: "v2.1.5",
    date: "2026/11/04",
    title: "細かな改善と不具合修正",
    isNew: true,
    highlights: [
      {
        text: "「商談一覧」の名前を「記録一覧」に変更しました。新しい議事録や、まだ商談記録にしていない議事録に印が付き、議事録に気づきやすくなります。",
      },
      {
        text: "日本語と英語がまじる会話でも、議事録・商談記録を作成できるようにしました。英語の発言は日本語に訳して記録します。あわせて、文字起こしの精度を高めました。",
        when: {
          minutesJaEnMixed: true,
          realtimeTranscribeGptTranscribe: true,
          transcribeGptTranscribe: true,
        },
      },
      {
        text: "1件の商談記録を、複数の案件フォルダに入れられるようにしました。議事録も案件フォルダに入れられ、フォルダ内で商談記録と並べて確認できます。仮登録の記録からも「どの案件の続きですか？」でフォルダに追加できます。",
        when: { caseFolder: true },
      },
      {
        text: "電波が弱い場所や圏外でも録音を端末に保存し、通信が回復した時点で自動で送信するようにしました。対話モードでは通信が切れると自動で一時停止し、回復後は続きから再開します。",
        when: { offlineRecordingSave: true },
      },
      {
        // OFF のテナントでは主な変更が1件になるので、ToDo 候補はここに置く
        text: "議事録のNextアクションのうち、ご自身が対応するものに「あなたのToDo候補」の印が付くようにしました。保存前のNextアクションも、そのままToDoに追加できます。",
      },
    ],
    improvements: [
      {
        text: "メールアドレスを登録していない方も、ログイン画面の「パスワードを忘れた」から、管理者にパスワードの再発行を依頼できるようにしました。",
        when: { passwordReissue: true },
      },
      {
        text: "顧客一覧で、顧客番号でも検索できるようにしました。",
        when: { customerNumberLabel: true, companyExternalSystemId: true },
      },
      {
        text: "複数の部署を兼務している場合、通知設定を部署ごとに分けられるようにしました。",
      },
      {
        text: "一覧から詳細を開いて戻ったとき、元の表示位置に戻るようにしました。",
      },
    ],
    bugfixes: [
      {
        text: "議事録から作成した商談記録に企業名や担当者を追加して保存すると、エラーになる不具合を修正しました。",
      },
      {
        text: "振り返りを録音すると、商談の録音が上書きされる場合がある不具合を修正しました。",
      },
      {
        text: "振り返りで、AIが記録の扱いについて話してしまう不具合と、音声ログの話者と内容がずれる不具合を修正しました。",
      },
      {
        text: "企業名を修正して本登録したあと、マイ手帳に修正前の企業名が表示される場合がある不具合を修正しました。",
      },
      {
        text: "連携したGoogleカレンダー・Microsoft 365の予定が、マイ手帳に表示されない場合がある不具合を修正しました。",
        when: { schedulingCalendarSync: true },
      },
    ],
  },
  {
    // PRODUCT_DEV-1178: タイトルには機能フラグの裏の機能を入れない。商談記録の作り直しは AI が作った記録だけなので
    // タイトルには大きすぎ、汎用の言い方にする（2026-10-26 日暮さん）。活動サマリは入口（メニュー）を添える。
    // 載せないもの: 集計ボード（ボードを置いた会社だけ）・対話の版 v5（一部のプリセットだけ）・見た目と表記の変更・
    // 管理者や運営だけの変更・お問い合わせ・細かい不具合の直し。
    version: "v2.1.4",
    date: "2026/10/26",
    title: "細かな改善と不具合修正",
    isNew: false,
    highlights: [
      {
        text: "録音や議事録から AI が作った商談記録を、作り直せるようにしました。商談記録の上の「再生成」からプリセットを選び直すと、AI がもう一度記録を作ります。企業名・担当者・振り返りはそのまま残り、作り直した記録は仮登録に戻ります。",
      },
      {
        text: "Google カレンダー・Microsoft 365 の予定表とつなげられるようにしました。つなぐとマイ手帳の時間割に予定が出て、予定からそのまま記録を始められます（管理者が会社として許可した場合に、設定画面からつなげます）。",
        when: { schedulingCalendarSync: true },
      },
      {
        text: "「グループ」を「案件フォルダ」に変え、お客さまとの続いている話をまとめておけるようにしました。フォルダの要約を見ながら「この案件の続きを記録する」から記録を始められます。",
        when: { caseFolder: true },
      },
    ],
    improvements: [
      {
        text: "議事録の詳細で、「更新する」を押す前の変更が分かるようにしました。保存しないまま画面を離れるときは確認が出ます。",
      },
      {
        text: "メニューの「活動サマリ」で、自分の記録件数をチームの平均・上位3人の平均と比べられるようにしました。予定はあったのに記録が無い件数も出ます。",
      },
      {
        text: "メニューや画面下のタブから移った画面で、「←」を押すとひとつ前に見ていた画面に戻るようにしました。",
      },
      {
        text: "振り返りを参加者それぞれが残せるようにしました。記録者以外の方も、自分の振り返りを話して残すか、「編集」から文字で書けます。",
      },
      {
        text: "マイ手帳の時間割に、予定なしで録った商談記録も出すようにしました。記録を長押しで動かすと、商談の時刻を変えられます。",
      },
      {
        text: "「どなたの記録ですか？」の候補に全店顧客番号を出し、同じお名前のお客さまを見分けられるようにしました。",
        when: { companyExternalSystemId: true },
      },
    ],
    bugfixes: [
      {
        text: "対話モードの開始直後に、AI が英語などで話し始めてしまう場合がある不具合を修正しました。",
      },
    ],
  },
  {
    // PRODUCT_DEV-1112: 新機能（議事録から商談記録の自動作成）は minutesMeetingLink の裏で、ON にするテナントが
    // 決まっていないので、タイトルはどの版にも使える汎用の言い方にする（2026-10-19 本蔵さん）。
    // 文体は案内文の書き言葉で、1項目1文の粗い粒度。小粒の表示直し・お問い合わせまわり（まだ使われていない）・
    // 「はじめての方へ」の更新・管理者や運営だけの変更は載せない。対話モードの細かい直しは1行にまとめる。
    version: "v2.1.3",
    date: "2026/10/20",
    title: "細かな改善と不具合修正",
    isNew: false,
    highlights: [
      {
        text: "議事録から商談記録を自動で作成できるようになりました。録音の終了後、議事録ができあがるまでの間にプリセットを選んでおくと、議事録の完成にあわせて商談記録の下書きも作成されます。",
      },
    ],
    improvements: [
      {
        text: "再ログイン時に、直前に見ていた画面へ戻るようにしました。",
      },
      {
        text: "録音中に電話がかかってきた場合、録音を自動で一時停止するようにしました。通話後に「再開」を押すと、続きから録音できます。",
      },
      {
        text: "対話モードで、AI の質問や会話の終了時の体験を改善しました。",
      },
      {
        text: "ToDo一覧の並び順を改善し、絞り込みに「3日以内」を追加しました。",
      },
      {
        text: "通知の一覧から、通知設定を開けるようにしました。",
      },
      {
        text: "「過去の日報・週報・月報」と「活動サマリ」を、メニューに移動しました。",
      },
      {
        text: "部のまとめの書き方を、部署ごとに「フォロー重視」「成果重視」「活動量重視」から選べるようにしました。",
      },
      {
        text: "複数の部署に所属している場合、部署ごとの日報をまとめて提出できるようにしました。",
      },
    ],
    bugfixes: [
      {
        text: "パスキーを使用していないブラウザで、ログイン時にパスキーの画面が自動で開く不具合を修正しました。",
      },
      {
        text: "対話モードで「対象外」「わからない」と記録した内容が、録音後に消える場合がある不具合を修正しました。",
      },
      {
        text: "振り返りを記録すると、商談記録の件名が日時に変わる場合がある不具合を修正しました。",
      },
    ],
  },
  {
    // PRODUCT_DEV-1109: 読む方の立場で「どの機能がどう変わったか」だけを書く（2026-10-13 日暮さん）。
    // 機能フラグの裏にあって使っているテナントがまだ無いもの（マイ手帳まわり・日程調整・Sansan・
    // 商談の振り返り）と、管理者だけの変更・細かい見た目の変更は載せない。
    // 「はじめての方へ」（onboarding）は OFF のテナントが残るので、タイトルには入れない。項目は ON のテナントにだけ出す。
    // お問い合わせは1文で、「AI がまず答える」の言い方で全テナントに出す。
    version: "v2.1.2",
    date: "2026/10/13",
    title: "お問い合わせを使いやすく・プリセットを選びやすく",
    isNew: false,
    highlights: [
      {
        text: "「はじめての方へ」を追加しました。メニューから開くと、基本の使い方を体験しながら確認できます。",
      },
      {
        text: "お問い合わせを使いやすくしました。送るとまず AI がお答えし、解決しないときはそのまま運営へ届きます。やり取りは1件の中で続けられます。",
      },
    ],
    improvements: [
      {
        text: "プリセットを選ぶ画面を見やすくしました。プリセットごとにアイコンが付きます。",
      },
      {
        text: "ToDoの期限を色で見分けられるようにしました（期限切れは赤・今日は橙・3日以内は黄）。",
      },
    ],
    bugfixes: [
      {
        text: "対話モードで、あとから話を足したときに、先に記録された内容が消えてしまう場合がある不具合を修正しました。",
      },
      {
        text: "議事録で、会議の日時がずれて表示される場合がある不具合を修正しました。",
      },
    ],
  },
  {
    // PRODUCT_DEV-1066: v2.01・v2.02・v2.1（ノート未作成のまま本番へ出た分）もここにまとめる。
    // 機能フラグの裏の機能は when で、そのフラグが合うテナントにだけ出す。
    version: "v2.1.1",
    date: "2026/10/06",
    title: "議事録を商談一覧に統合・記録の公開範囲を設定可能に・ToDoの削除に対応",
    isNew: false,
    highlights: [
      {
        text: "議事録を「商談一覧」に統合しました。画面上部の「議事録｜商談一覧」で切り替えられ、商談記録と同じく「自分／チーム／すべて」で絞り込めます。",
      },
      {
        text: "議事録と商談記録に「公開範囲」を設定できるようにしました。「公開」「限定公開（お選びになった方だけ）」「非公開」から選べます。",
      },
      {
        text: "ToDoを削除できるようにしました。ゴミ箱やスワイプで削除でき、削除してから5秒間は元に戻せます。右へスワイプすると完了にできます。",
      },
      {
        text: "「マイ手帳」を追加しました。アプリを開くと最初に表示され、その日の予定・ToDo・メモを1ページで確認できます。予定からそのまま録音や記録を始められます。",
      },
    ],
    improvements: [
      {
        text: "対話モードで、必須の項目にお答えが無いときは、AIが聞き直すようにしました。",
      },
      {
        text: "録音中の操作ボタンを画面の下に固定し、押しやすくしました。",
      },
    ],
    bugfixes: [
      {
        text: "日本語の変換を確定する Enter で、項目が登録されてしまう不具合を修正しました。",
      },
      {
        text: "対話モードで、すべての項目にお答えいただく前に会話が終わってしまう場合がある不具合を修正しました。",
      },
    ],
  },
  {
    version: "v2.0",
    date: "2026/09/01",
    title: "マイページ（β版）を追加・日報と日程調整・設定の端末間の引き継ぎ",
    isNew: false,
    highlights: [
      {
        text: "マイページ（β版）を追加しました。画面下のメニューに「マイページ」が加わり、今日の予定・日報・日程調整・活動サマリを1つの画面から確認できます。（※マイページと、その中の日報・日程調整は現在β版です。準備ができたテナント様から順次ご利用いただけるようになります）",
      },
      {
        text: "日報（β版）を追加しました。その日の商談記録から日報の下書きが自動で作られ、週報・月報としても振り返れます。本文のもとになった記録から、実際の商談記録へそのまま移動できます。",
      },
      {
        text: "日程調整（β版）を追加しました。候補の日時を作ってお客様へリンクをお送りすると、お客様はログインなしで候補ごとに ○ × でご回答いただけます。確定・日時の変更・取りやめは、メールとカレンダーファイル（.ics）でお相手に届きます。",
      },
      {
        text: "通知バッジ・お知らせの既読・AI音声の音量・検索履歴・記録モードの選択などの設定を、アカウントに保存するようにしました。端末やブラウザを変えても、同じ設定でご利用いただけます。",
      },
    ],
    improvements: [
      {
        text: "商談記録・顧客一覧・録音の各画面で、検索の入口と並び替えの見せ方を揃えました。並び替えはボタンで選べるようになっています。",
      },
    ],
    bugfixes: [
      {
        text: "「商談の振り返りを記録」で、議事録の内容ではなく商談記録の項目についての質問に逸れてしまう不具合を修正しました。",
      },
      {
        text: "商談の記録が「処理中」のまま完了しない場合がある不具合を修正しました。すでに残ってしまっている記録も自動で回収されます。",
      },
      {
        text: "ヒアリングメモに、実際には話していない文言が混ざる場合がある不具合を修正しました。",
      },
      {
        text: "未読のリリースノートがあっても、メニューに未読の印が表示されない不具合を修正しました。",
      },
    ],
  },
  {
    version: "v1.9.1",
    date: "2026/08/25",
    title:
      "デフォルトプリセットを「商談記録（法人）」に改名・お客様情報の紐づけ改善・問い合わせ前にAIへ質問可能に",
    isNew: false,
    highlights: [
      {
        text: "何が選択されているか分かりやすくなるよう、プリセット「デフォルト」の名称を「商談記録（法人）」に変更しました。",
      },
      {
        text: "商談の詳細・編集画面で、お客様の紐づけ確認をよりスムーズにしました。編集画面でその場で候補を確認・選択でき、詳細画面でもお客様マスタの登録内容を確認できます。",
      },
      {
        text: "「メニュー」→「お問い合わせ」から、分からない事を「AIへ質問」できるようになりました。AIに質問しても解決しない場合は、そのままお問い合わせフォームへ進めます。",
      },
    ],
    improvements: [
      {
        text: "使い方ガイドの内容を最新の情報に更新しました。",
      },
    ],
    bugfixes: [
      {
        text: "まれに画面の切り替えが止まったまま戻らなくなる不具合を修正しました。",
      },
      {
        text: "削除した議事録がゴミ箱に表示されず、復元・完全削除ができない不具合を修正しました（「議事録」タブを追加しています）。",
      },
      {
        text: "ご自身以外の商談を編集・削除しようとした際に、操作できない理由が表示されるようになりました。",
      },
      {
        text: "通知の件数が多い場合に、一部の通知が一覧に表示されない不具合を修正しました。",
      },
      {
        text: "商談記録の項目が、すべてカタカナで記録される場合がある不具合を修正しました。",
      },
      {
        text: "対話モードで、同じ項目について2回お答えいただいた際、後から伝えた内容が記録に反映されない不具合を修正しました。",
      },
    ],
  },
  {
    version: "v1.8.3",
    date: "2026/08/04",
    title: "検索機能の改善・対話キャラクターの追加・顧客レポート（β版）のリニューアル",
    isNew: false,
    highlights: [
      {
        text: "「検索」機能を改善し、検索対象をメモ・議事録などにも拡大しました。あわせて絞り込み条件（種類・プリセットなど）を「詳細フィルタ」に整理し、検索結果の表示も見やすくなりました。",
      },
      {
        text: "対話モードで記録する際の対話相手として、女性キャラクターを追加しました。あわせて、各画面に適宜、Unizoロゴから誕生したゾウのキャラクターを追加しました。",
      },
      {
        text: "顧客情報に「その他ヒアリングできた事項」欄を追加しました。既存項目に当てはまらない情報がある場合でも、情報が保存されやすくなります。",
      },
    ],
    improvements: [
      {
        text: "顧客レポート機能（β版）の画面デザインを見やすく刷新し、情報充足度が正しく表示されない不具合も修正しました。(※本機能は現在β版として提供しております)",
      },
      {
        text: "画面のUnizoロゴを押した場合、HOMEに遷移できるようになりました。",
      },
    ],
    bugfixes: [
      {
        text: "Android端末の対話モードで、ご利用の環境によって、AIの声が聞こえない場合がある不具合を修正しました。",
      },
    ],
  },
  {
    version: "v1.8.2",
    date: "2026/07/28",
    title: "お問い合わせ機能の追加・録音対話の品質改善",
    isNew: false,
    highlights: [
      {
        text: "お問い合わせ機能を追加しました。メニューの「お問い合わせ」から、操作方法のご質問や不具合のご報告をアプリ内で送信できるようになりました。回答が届くと通知でお知らせします。解決しなかった場合は、元のお問い合わせの続きとして再度送信できます。",
      },
    ],
    improvements: [
      {
        text: "録音対話で、顧客マスタに登録済みのお客様情報をAIが一つずつ読み上げて確認することがある挙動を見直し、一言に留めるようにしました。",
      },
    ],
    bugfixes: [
      {
        text: "録音対話で、AIが不要なシステム文言を話してしまうことがある不具合や、すべての項目に回答しても記録が自動で終了しないことがある不具合を修正しました。",
      },
      {
        text: "「過去の記録から作成」で記録を開始した際に、引き継いだお客様情報や担当者が正しく引き継がれないことがある不具合を修正しました。",
      },
      {
        text: "商談タイトルが長い場合に画面が横に崩れる不具合を修正しました。あわせて、商談詳細ではタイトルが省略されず全文折り返して表示されるようになりました。",
      },
    ],
  },
  {
    version: "v1.8.1",
    date: "2026/07/21",
    title: "議事録の編集機能・会社名検索の改善・細かな使い勝手の向上",
    isNew: false,
    highlights: [
      {
        text: "議事録の「決定事項」「Nextアクション」を、画面上でそのまま編集できるようになりました。AIが整理した内容をタップして修正でき、担当者も本文にあわせて記録されます。",
      },
      {
        text: "商談記録時の会社名検索を改善しました。本社所在地に近い企業が優先して表示されるようになり、似た名前の会社が多い場合でも目的の企業を見つけやすくなりました。",
      },
      {
        text: "自由記録モードの記録整理を改善しました。話した内容を項目ごとに整理する処理を見直し、担当者などの情報が漏れたり重複したりしにくくなりました。",
      },
    ],
    improvements: [
      {
        text: "商談記録・メモ一覧の並び替えをプルダウンで選べるようにし、「顧客登録順」の並び替えを追加しました。",
      },
      {
        text: "自由記録モードの録音中のプリセット変更の操作を分かりやすくし、画面に表示されているプリセットの内容がそのまま保存されるようにしました。",
      },
    ],
    bugfixes: [
      {
        text: "iPadで商談記録のコピーが「コピーに失敗しました」となることがある不具合を修正しました。",
      },
      {
        text: "「過去の記録から作成」で商談を記録した際、選択した担当者がカタカナ表記で重複して登録されてしまう不具合を修正しました。",
      },
      {
        text: "議事録の詳細画面からブラウザの「戻る」で戻ると、メモ一覧に移動してしまう不具合を修正しました。",
      },
      {
        text: "Androidのホーム画面でアプリのアイコンが大きく表示されてしまう問題を修正しました。",
      },
    ],
  },
  {
    version: "v1.8.0",
    date: "2026/07/13",
    title: "顧客一覧画面の追加・議事録機能の追加・Unizoロゴの刷新",
    isNew: false,
    highlights: [
      {
        text: "顧客一覧画面を追加しました。自分が担当する顧客を一覧で管理でき、顧客を指定して商談を記録できます。また、担当顧客を引き継げる機能を使用することで、すぐに引き継いだ顧客の商談記録にアクセスできます。",
      },
      {
        text: "議事録機能を追加しました。「録音」タブから、長時間の会議（最大60分）を録音することができます。録音終了後はAIが「要約・決定事項・Nextアクション」を自動で整理します。Nextアクションは自身のToDoとして登録することで、会議後のフォローに活用できます。",
      },
      {
        text: "Unizoのロゴを刷新しました。「聞いたことを決して忘れない」と言われている象をモチーフに、音声の波形を印象づけるロゴになっています。",
      },
    ],
    improvements: [
      {
        text: "トップページの商談記録から、直接商談内容のコピーや商談記録の編集を行えるようにしました。",
      },
      {
        text: "すべての画面のヘッダーデザインを統一しました。どの画面でも同じ場所で迷わず操作できるようになりました。",
      },
    ],
    bugfixes: [
      {
        text: "メモ機能の文字起こしに、管理者が登録した辞書（専門用語の表記）が反映されない不具合を修正しました。",
      },
      {
        text: "使い方ガイドの記事タイトルが二重に表示される不具合を修正しました。",
      },
    ],
  },
  {
    version: "v1.7.5",
    date: "2026/07/06",
    title: "音声記録の精度向上・メモ/ToDoの改善・顧客設定の簡易化・コピー機能の改善",
    isNew: false,
    highlights: [
      {
        text: "音声記録を改善しました。音声認識モデルと文字起こし・整理の処理を全面的に見直し、会話から項目へ整理する際の情報の抜け漏れを減らして、商談記録の精度を高めました。意味の通らない文言も記録されにくくなりました。",
      },
      {
        text: "音声メモ・ToDo機能の使い勝手を改善しました。AIが提案するToDo候補を再生成でき、削除した音声メモをゴミ箱から戻せるようになりました。記録の開始や整形中の表示もよりスムーズになりました。",
      },
      {
        text: "企業担当者や個人のお客様を、かんたんに設定できるようになりました。顧客マスタで企業だけでなく個人も管理でき、音声で聞き取った担当者名を登録済みの漢字表記で自動表示するなど、登録の手間を減らしました。",
      },
    ],
    improvements: [
      {
        text: "商談記録のテキストコピーを改善しました。定型文フォーマットに沿った整形、コピー内容の文字数の最適化、余分な記号や「未設定」項目の削減に対応し、そのまま貼り付けやすくなりました。",
      },
      {
        text: "顧客マスタの個人のお客様の情報を充実させ、登録できる項目を増やしました。",
      },
      {
        text: "部署を設定できるようになり、部署ごとに利用するプリセットを絞り込めるようになりました。所属部署に応じて、記録開始時に適したプリセットが表示されます。",
      },
      {
        text: "ログイン状態がより長く維持されるようになりました。また、アプリの最新版がより早く反映されるようになりました。",
      },
    ],
    bugfixes: [
      {
        text: "検索結果から商談詳細を開いた後、「戻る」で元の検索結果に戻れるようになりました。",
      },
      {
        text: "商談一覧に一部の商談が表示されないことがある不具合を修正しました。",
      },
    ],
  },
  {
    version: "v1.7.0",
    date: "2026/06/01",
    title: "メモ機能の追加・HOME/検索画面の改善・通知リニューアル",
    isNew: false,
    highlights: [
      {
        text: "HOME画面と検索画面のUIを改善しました。より少ないタップで商談記録を開始でき、検索性も向上しました。",
      },
      {
        text: "メモ機能を追加しました。音声でメモを残し、企業に紐づけて管理したり、記録したメモからToDoを作成できるようになりました。",
      },
      {
        text: "通知機能をリニューアルしました。他のメンバーの商談作成や、時間のかかる処理の完了に気づきやすくなりました。",
      },
    ],
    improvements: [
      {
        text: "管理者により「短縮ユーザー名」でのログイン設定がされている場合、「メニュー」の「基本設定」から、次回以降のログインがワンタップでアクセスできるようになる機能が追加されました。",
      },
      {
        text: "音声記録（文字起こし）の精度を向上しました。",
      },
    ],
    bugfixes: [
      {
        text: "ログアウト後にホーム画面のアプリを再起動すると、ログイン状態が残ってしまうことがある不具合を解消しました。",
      },
    ],
  },
  {
    version: "v1.6.0",
    date: "2026/05/11",
    title: "パスキー設定で簡単ログインが可能に",
    isNew: false,
    highlights: [
      {
        text: "ログイン時にパスキー設定（指紋認証や顔認証）ができ、簡単にログインできるようになりました。パスキー管理（追加や削除）は「メニュー」の「基本設定」から行えます。",
      },
    ],
    improvements: [
      {
        text: "Safariブラウザでの音声録音（自由記録モード含む）に対応しました。",
      },
      {
        text: "Androidスマートフォンでも、商談記録のドラッグ＆ドロップ操作ができるようになりました。",
      },
    ],
    bugfixes: [
      {
        text: "商談記録の選択時に、所属フォルダの情報が非表示になる不具合を解消しました。",
      },
      {
        text: "ログイン情報を保持したにもかかわらず、保持されないことがある不具合を解消しました。",
      },
    ],
  },
  {
    version: "v1.5.0",
    date: "2026/04/19",
    title: "グループ要約機能・基本設定機能・お知らせページ新設",
    highlights: [
      {
        text: "「商談グループ」内にある複数の商談記録の情報をまとめる要約機能ができました。",
      },
      {
        text: "メニューに基本設定ページを追加しました。ユーザー情報の確認とパスワード変更ができるようになりました。",
      },
      {
        text: "お知らせページを新設しました。本リリースノートもここに集約されました。",
      },
    ],
    improvements: [
      {
        text: "商談記録を編集する際、顧客マスタに未登録の企業を公開データベース（gBizInfo）から選択して正規登録できるようになりました。",
      },
      {
        text: "ドラッグ&ドロップで商談記録のグループ移動ができるようになりました。(※Androidのみ非対応)",
      },
      {
        text: "自由記録モードの記録完了後、すぐに次の画面へ移動できるようになりました。",
      },
      {
        text: "自由記録モードの記録中に、登録プリセットに応じて画面表示を切り替えられるようになりました。",
      },
    ],
    bugfixes: [],
  },
  {
    version: "v1.4.0",
    date: "2026/04/02",
    title: "商談日時編集・絞り込み機能改善",
    highlights: [
      {
        text: "絞り込み機能を改善しました。絞り込みバーのデザイン刷新・検索条件チップの折り返し対応・件数表示に対応しました。",
      },
      {
        text: "商談日時を編集できるようになりました。",
      },
    ],
    improvements: [
      {
        text: "商談グループフィルターの件数がリアルタイムで反映されるようになりました。",
      },
      {
        text: "ダッシュボードの絞り込みのデフォルトを「全員」に変更しました。",
      },
      {
        text: "商談詳細画面のUIを改善しました。",
      },
    ],
    bugfixes: [
      {
        text: "商談グループからの記録開始時にモード選択が表示されるようになりました。",
      },
    ],
  },
  {
    version: "v1.3.0",
    date: "2026/03/19",
    title: "商談相手をコピーして記録・自由記録モード対応・ゴミ箱機能",
    highlights: [
      {
        text: "過去の商談をベースに、商談相手をコピーして記録できる機能を追加しました。",
      },
      {
        text: "慣れてきた方や少しでも時短したい方向けに、AIとの対話なしに商談を記録できる自由記録モードを追加しました。",
      },
      {
        text: "ゴミ箱機能を追加しました。最大30日間保管され、削除した商談を復元もできます。",
      },
    ],
    improvements: [
      {
        text: "役職のみの担当者情報も記録できるようになりました。",
      },
      {
        text: "より使いやすくなるようデザインを修正しました。",
      },
    ],
    bugfixes: [
      {
        text: "録音解析中にリダイレクトが繰り返される問題を修正しました。",
      },
    ],
  },
  {
    version: "v1.2.0",
    date: "2026/03/06",
    title: "お気に入り・閲覧履歴・検索条件履歴",
    highlights: [
      {
        text: "お気に入り機能を追加しました。重要な商談や商談グループを最大50件まで登録でき、すばやくアクセスできます。",
      },
      {
        text: "閲覧履歴機能を追加しました。最近見た商談を最新50件までアクセスできます。",
      },
      {
        text: "検索条件の履歴が最大10件まで自動保存されます。よく使う検索条件はピン留めすることができます。",
      },
    ],
    improvements: [
      {
        text: "商談検索結果でグループ内の商談を個別に表示できるようになりました。",
      },
      {
        text: "録音画面にミュートボタンを追加しました。",
      },
      {
        text: "商談詳細画面のUIをカード形式にリニューアルしました。",
      },
    ],
    bugfixes: [
      {
        text: "録音再開時やブラウザバック時に音声が二重再生される問題を修正しました。",
      },
    ],
  },
  {
    version: "v1.1.0",
    date: "2026/02/16",
    title: "商談グループ・自動抽出・絞り込み",
    highlights: [
      {
        text: "商談グループ機能を追加しました。商談を長押しすると任意の商談グループに移動させることができます。",
      },
      {
        text: "商談データの自動抽出（法人名・担当者・ニーズ・次のアクション）に対応しました。",
      },
      {
        text: "自分が登録した商談、自分以外が登録した商談を1Clickで絞り込めるようになりました。",
      },
    ],
    improvements: [
      {
        text: "商談記録のタイトルが商談内容から自動生成されるようになりました。",
      },
      {
        text: "IPホワイトリスト機能を追加しました。",
      },
    ],
    bugfixes: [
      {
        text: "商談記録中に画面がOFFになると中断される問題を解消しました。",
      },
    ],
  },
  {
    version: "v1.0.0",
    date: "2026/01/26",
    title: "Unizoリリース",
    highlights: [
      {
        text: "Unizoがリリースされました！画面下部の「＋」アイコンから、音声入力で商談の内容、顧客の反応、ネクストアクションなどを記録できます。",
      },
    ],
    improvements: [],
    bugfixes: [],
  },
];
